import type { CSSProperties, ReactNode } from 'react';
import type { Icon as TablerIcon } from '@tabler/icons-react';
import { TextPreset } from '@/constants/componentPresets';
import type { ColorType } from '@/theme/colors';
import type { FontWeight } from '@/theme/typography';
import { Cicon } from '../Cicon';
import { Ctext } from '../Ctext';
import { BUTTON_ICON_POSITION, type ButtonIconPosition } from './buttonTypes';

interface RenderButtonIconInput {
  icon?: TablerIcon;
  iconSize: number;
  iconColor?: ColorType;
  defaultIconColor: ColorType;
  iconOpacity?: number;
  iconStyle?: CSSProperties;
}

interface RenderButtonTextInput {
  label?: string;
  textSize?: number;
  defaultTextSize?: number;
  textWeight: FontWeight;
  textColor?: ColorType;
  defaultTextColor: ColorType;
}

interface RenderDefaultButtonContentInput {
  icon?: TablerIcon;
  iconPosition: ButtonIconPosition;
  iconElement: ReactNode;
  textElement: ReactNode;
}

interface RenderButtonContentInput extends RenderDefaultButtonContentInput {
  children?: ReactNode;
}

export function renderButtonIcon({
  icon,
  iconSize,
  iconColor,
  defaultIconColor,
  iconOpacity,
  iconStyle,
}: RenderButtonIconInput): ReactNode {
  if (!icon) {
    return null;
  }

  return (
    <Cicon
      icon={icon}
      size={iconSize}
      color={iconColor ?? defaultIconColor}
      opacity={iconOpacity}
      style={iconStyle}
    />
  );
}

export function renderButtonText({
  label,
  textSize,
  defaultTextSize,
  textWeight,
  textColor,
  defaultTextColor,
}: RenderButtonTextInput): ReactNode {
  if (!label) {
    return null;
  }

  return (
    <Ctext
      preset={TextPreset.Regular}
      size={textSize ?? defaultTextSize ?? 16}
      weight={textWeight}
      color={textColor ?? defaultTextColor}
      style={{ whiteSpace: 'nowrap' }}
    >
      {label}
    </Ctext>
  );
}

export function renderDefaultButtonContent({
  icon,
  iconPosition,
  iconElement,
  textElement,
}: RenderDefaultButtonContentInput): ReactNode {
  return (
    <>
      {icon && iconPosition === BUTTON_ICON_POSITION.Left && iconElement}
      {textElement}
      {icon && iconPosition === BUTTON_ICON_POSITION.Right && iconElement}
    </>
  );
}

export function renderButtonContent({
  children,
  icon,
  iconPosition,
  iconElement,
  textElement,
}: RenderButtonContentInput): ReactNode {
  if (children) {
    return children;
  }

  return renderDefaultButtonContent({
    icon,
    iconPosition,
    iconElement,
    textElement,
  });
}
